import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Stack, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LoginForm } from '../../../sections/auth/login';
import Logo from '../../../components/Logo';
import axios from '../../../utils/axios';
import { getFullDomain } from '../../../utils/fullDomain';

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [campusInfo, setCampusInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fullDomain = getFullDomain();
  const storedCampusInfo = JSON.parse(localStorage.getItem('campusInfo'));

  const updateFavicon = (faviconUrl) => {
    if (!faviconUrl) return;

    const favicons = document.querySelectorAll("link[rel*='icon']");
    favicons.forEach((favicon) => {
      favicon.href = faviconUrl;
    });

    if (favicons.length === 0) {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    }
  };

  const getCampusInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/accounts/whitelabel/info/${fullDomain}/`);

      if (response.data && Object.keys(response.data).length > 0) {
        setCampusInfo(response.data);
        localStorage.setItem('campusInfo', JSON.stringify(response.data));
        updateFavicon(response.data.favicon);
      }
    } catch (err) {
      if (err.error === 'Invalid slug') {
        navigate('/unknown', { replace: true });
      }
      setError(err);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = localStorage.getItem('campusInfo');
    if (cached) {
      const parsedCache = JSON.parse(cached);
      setCampusInfo(parsedCache);
      updateFavicon(parsedCache.favicon);
      setLoading(false);
    }

    getCampusInfo();
  }, []);

  const LogoSection = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (campusInfo?.logo) {
      return (
        <img
          src={campusInfo.logo}
          alt="Campus Logo"
          style={{ width: 220, height: 100, objectFit: 'contain' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
            return <Logo />;
          }}
        />
      );
    }

    return <Logo />;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[100],
        py: 12,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            backgroundColor: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Stack spacing={4} alignItems="center">
            <LogoSection />

            <Stack spacing={1} alignItems="center" sx={{ width: '100%' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              >
                Kiosk Login
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                Please sign in to continue
              </Typography>
            </Stack>

            {/* Login Form Component */}
            <Box sx={{ width: '100%' }}>
              <LoginForm kiosk />
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default LandingPage;
